import { formatCents, toE164, tt } from '@agos/shared'

/**
 * `/settings-sms` — five tabs over two endpoints, and every button on it spends money.
 *
 * **FB-141.** The *Náhľad správy* shows a message the customer will never receive. `SmsGateway` strips
 * diacritics before sending, and its own comment says why:
 *
 * > An SMS carrying `ľ` or `š` leaves the GSM-7 alphabet and becomes a Unicode message, which holds 70
 * > characters per segment instead of 160 — so a delivery notice that fitted in one segment becomes three,
 * > and the restaurant pays three times for it.
 *
 * Every template on this page is written with accents, so the preview is wrong for every one of them:
 * *Dobrý deň Ján Novák, vaša objednávka č. ORD-001…* is delivered as *Dobry den Jan Novak, vasa objednavka
 * c. ORD-001…*. Two more of the same kind: the **sender name** field allows 13 characters where BulkGate and
 * the backend both truncate at **11** — the stored *Pizza Majstro* reaches the customer as *Pizza Majst* —
 * and `charCount` counts the **template** rather than the rendered message and divides by 160, where a
 * concatenated GSM-7 text carries **153** characters per part. The two disagree from 307 characters up.
 *
 * **FB-142.** The *Manuálna SMS* tab's *Načítať zákazníkov* button fetches
 * `GET /customers?phoneOnly=true&limit=9999` and puts **every** phone number in the recipient box —
 * **2 463** of them on the restored data, **2 341** distinct once normalised the way the gateway normalises
 * them, so 122 people would be texted twice. One click on *Odoslať SMS* then sends to all of them. Nothing
 * states the cost: at the €0,026 the page itself quotes, that is **60,87 €**. And `sendManual` caps the list
 * at **500**, rejecting the whole request with a 400 — so the operator's only feedback for a 2 463-number
 * send is *Chyba odosielania*, with nothing sent and nothing said about why.
 *
 * **FB-143.** `GET /settings/sms` answers the **unmasked 50-character `bulkgateAppToken`** — real
 * credentials on the owner's paid BulkGate account — which legacy renders in a password input with a reveal
 * button and POSTs back on **every** save. `SmsSettingsService.update` writes only the keys the body names
 * (`if (!body.containsKey(field)) continue`), so it never has to be sent at all.
 */

export type SmsTab = 'delivery' | 'delay' | 'loyalty' | 'portal' | 'manual' | 'contacts'

// §9.241 — `contacts` is the sixth. Legacy pins its contact book to the bottom of one long SMS tab;
// this page is already tabbed, so it becomes a tab rather than a footer nobody scrolls to.
export const SMS_TABS: readonly SmsTab[] = [
  'delivery',
  'delay',
  'loyalty',
  'portal',
  'manual',
  'contacts',
]

export function tabLabel(tab: SmsTab): string {
  if (tab === 'delivery') return tt('SMS doručenie')
  if (tab === 'delay') return tt('SMS meškanie')
  if (tab === 'loyalty') return tt('Vernostné')
  if (tab === 'portal') return 'BulkGate'
  if (tab === 'manual') return tt('Manuálna SMS')
  return tt('SMS kontakty')
}

// ─── §14.7: the price of one text, in cents ───────────────────────────────────

/** BulkGate's Slovak rate, the figure legacy's own page quotes. */
export const SMS_PRICE_CENTS = 2.6

/** `sendManual` refuses a longer list outright, before sending anything. */
export const MAX_RECIPIENTS = 500

/** `sendManual` refuses a longer message. */
export const MAX_MESSAGE_CHARS = 1600

export const SENDER_NAME_LIMIT = 11

/** One GSM-7 text, and one part of a concatenated one — the UDH costs seven characters. */
export const GSM_SINGLE = 160
export const GSM_CONCATENATED_PART = 153

export interface SmsForm {
  deliveryEnabled: boolean
  deliverySenderName: string
  deliveryTemplate: string
  /** §9.322 - the *driver is at your door* text. On the wire since the port and on no screen. */
  atAddressEnabled: boolean
  atAddressTemplate: string
  delayEnabled: boolean
  delayThresholdMinutes: number
  delayTemplate: string
  delayMode: 'auto' | 'manual'
  bulkgateAppId: string
}

export const EMPTY_SMS: SmsForm = {
  deliveryEnabled: false,
  deliverySenderName: '',
  deliveryTemplate: '',
  atAddressEnabled: false,
  atAddressTemplate: '',
  delayEnabled: false,
  delayThresholdMinutes: 50,
  delayTemplate: '',
  delayMode: 'manual',
  bulkgateAppId: '',
}

/** The fields this page edits. `bulkgateAppToken` is deliberately not among them — FB-143. */
export const SMS_FIELDS_USED = Object.keys(EMPTY_SMS)

export const TOKEN_FIELD = 'bulkgateAppToken'

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function formOf(settings: Record<string, unknown> | undefined): SmsForm {
  const minutes = settings?.delayThresholdMinutes
  return {
    deliveryEnabled: settings?.deliveryEnabled === true,
    deliverySenderName: text(settings?.deliverySenderName),
    deliveryTemplate: text(settings?.deliveryTemplate),
    atAddressEnabled: settings?.atAddressEnabled === true,
    atAddressTemplate: text(settings?.atAddressTemplate),
    delayEnabled: settings?.delayEnabled === true,
    delayThresholdMinutes: typeof minutes === 'number' && Number.isFinite(minutes) ? minutes : 50,
    delayTemplate: text(settings?.delayTemplate),
    delayMode: settings?.delayMode === 'auto' ? 'auto' : 'manual',
    bulkgateAppId: text(settings?.bulkgateAppId),
  }
}

/** Whether the stored settings carry a token — knowable without reading it. */
export function carriesToken(settings: Record<string, unknown> | undefined): boolean {
  const value = settings?.[TOKEN_FIELD]
  return typeof value === 'string' && value.trim() !== ''
}

// ─── FB-141: what actually leaves ─────────────────────────────────────────────

export const SMS_PLACEHOLDERS: readonly string[] = ['meno', 'adresa', 'cislo_objednavky']
export const POINTS_SMS_PLACEHOLDERS: readonly string[] = ['meno', 'body', 'zostatok']

/**
 * **`campaign_sms_template` deliberately has no editor - §9.322.**
 *
 * It is on the wire and in `sms_settings`, legacy edits it on its loyalty page, and it looked like a
 * missed port. It is not: `grep -rn "campaign_sms_template"` across the backend matches **one** line,
 * the column-name map in `SmsSettingsService`. Nothing reads it, and `CampaignRunner` has no
 * `SmsGateway` at all - `channelOn(settings, "sms")` is a gate in front of a sender that does not
 * exist, so a campaign's SMS channel can be switched on and no text is ever sent.
 *
 * A box here would write a dead column, which is precisely the defect FB-463 found and FB-509
 * fixed. Recorded in `OWNER-QUESTIONS.md` instead: whether campaign SMS should exist is the owner's
 * call, because BulkGate charges per message and a blast is thousands of them.
 */

/**
 * The sample values, accented — because customers are called Ján Novák and live on Hlavná.
 *
 * One set for both previews. Using an unaccented sample for the *sent* preview and an accented one for the
 * *typed* preview would manufacture a difference out of the fixture rather than showing the gateway's.
 *
 * Resolved per call rather than at module load: `tt()` at load time freezes whichever language happened to
 * be ready, which is FB-137's defect one file over.
 */
function sampleValues(kind: 'order' | 'points'): Readonly<Record<string, string>> {
  if (kind === 'points') {
    return { meno: tt('Ján Novák'), body: '50', zostatok: '250' }
  }
  return {
    meno: tt('Ján Novák'),
    adresa: tt('Hlavná 1, Bratislava'),
    cislo_objednavky: 'ORD-001',
  }
}

/**
 * The gateway's own transformation, mirrored.
 *
 * `stripDiacritics(message.text())` with `unicode: false`. Reproduced rather than guessed at, because the
 * preview is only worth anything if it is the same string.
 */
export function asSent(value: string): string {
  return value.normalize('NFD').replace(/\p{Mn}+/gu, '')
}

/** Substitute the sample values, then strip — in that order, because a sample value can carry accents too. */
export function previewOf(template: string, kind: 'order' | 'points' = 'order'): string {
  const values = sampleValues(kind)
  const substituted = template.replace(/\{(\w+)}/g, (whole, name: string) => values[name] ?? whole)
  return asSent(substituted)
}

/** What the typed template looks like before the gateway touches it — for showing the difference. */
export function typedPreviewOf(template: string, kind: 'order' | 'points' = 'order'): string {
  const values = sampleValues(kind)
  return template.replace(/\{(\w+)}/g, (whole, name: string) => values[name] ?? whole)
}

/**
 * Whether the **wording somebody typed** will be changed on the way out.
 *
 * Compared against the template alone, not against a preview: a substituted customer name loses its accents
 * too, and mixing the two would mean a warning about my sample data instead of about their wording. The two
 * previews show the name; this decides whether to say anything about the template.
 */
export function accentsWouldBeStripped(template: string): boolean {
  return asSent(template) !== template
}

/**
 * Characters that survive the accent stripping and still are not GSM-7.
 *
 * The gateway sends `unicode: false`, so BulkGate has to substitute or drop them. A `€` or a curly quote in
 * a template is silently something else by the time it arrives.
 */
// Everything the GSM-7 default alphabet and its escape table do **not** cover (3GPP TS 23.038). Written as
// a regular expression rather than a string of characters: it is a character set, not translatable text,
// and a regex literal keeps the no-inline-Slovak rule looking at prose instead of at an alphabet.
const NOT_GSM7 =
  /[^@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?¡A-ZÄÖÑÜ§¿a-zäöñüà^{}\\[~\]|€]/gu

export function nonGsmCharacters(value: string): string[] {
  return [...new Set(asSent(value).match(NOT_GSM7) ?? [])]
}

export interface SmsCost {
  /** Characters in the message that will actually be sent. */
  characters: number
  /** Segments BulkGate will bill for. */
  segments: number
  /** What legacy's `Math.ceil(len / 160)` on the raw template would have said. */
  legacySegments: number
}

/**
 * Segments, counted the way they are billed.
 *
 * A single GSM-7 text holds 160 characters. The moment it needs a second, each part carries a
 * concatenation header and holds **153** — so 306 characters is two texts and 307 is three. Legacy divides
 * the *template* by 160, which is the wrong string and the wrong divisor, and the two answers diverge from
 * 307 characters up.
 */
export function costOf(template: string, kind: 'order' | 'points' = 'order'): SmsCost {
  const sent = previewOf(template, kind)
  const characters = sent.length
  const segments =
    characters === 0
      ? 0
      : characters <= GSM_SINGLE
        ? 1
        : Math.ceil(characters / GSM_CONCATENATED_PART)
  return {
    characters,
    segments,
    legacySegments: template.length === 0 ? 0 : Math.ceil(template.length / GSM_SINGLE),
  }
}

export function costLabel(cost: SmsCost): string {
  return `${cost.characters} ${tt('znakov')} · ${cost.segments} SMS · ${formatCents(Math.round(cost.segments * SMS_PRICE_CENTS))}`
}

/** Whether the counter and the bill disagree — worth saying when they do. */
export function countingDisagrees(cost: SmsCost): boolean {
  return cost.segments !== cost.legacySegments
}

/** The sender name as it arrives: accents gone, cut to eleven. */
export function senderAsSent(name: string): string {
  const stripped = asSent(name.trim())
  return stripped.length > SENDER_NAME_LIMIT ? stripped.slice(0, SENDER_NAME_LIMIT) : stripped
}

export function senderWouldChange(name: string): boolean {
  return name.trim() !== '' && senderAsSent(name) !== name.trim()
}

export function unknownPlaceholders(template: string, known: readonly string[]): string[] {
  const found = [...template.matchAll(/\{(\w+)}/g)].map((match) => match[1] ?? '')
  return [...new Set(found.filter((name) => !known.includes(name)))]
}

// ─── FB-142: the recipient list ───────────────────────────────────────────────

export interface Recipients {
  /** Entries as typed or loaded. */
  entries: readonly string[]
  /** How many distinct people that is, after the gateway's own normalisation. */
  people: number
  /** Entries that normalise onto a number already in the list. */
  duplicates: number
  /** Entries that are not a phone number at all. */
  unusable: readonly string[]
  overCap: boolean
  costCents: number
}

/**
 * What a recipient list really is.
 *
 * `toE164` is the same normalisation `SmsGateway.internationalise` applies, so two spellings of one number
 * are one text and one charge — and one person receiving the same message twice if both spellings are in
 * the list.
 */
export function recipientsOf(entries: readonly string[]): Recipients {
  const seen = new Set<string>()
  const unusable: string[] = []
  let duplicates = 0

  for (const entry of entries) {
    const normalised = toE164(entry)
    if (normalised === null) {
      unusable.push(entry)
      continue
    }
    if (seen.has(normalised)) {
      duplicates += 1
      continue
    }
    seen.add(normalised)
  }

  return {
    entries,
    people: seen.size,
    duplicates,
    unusable,
    overCap: entries.length > MAX_RECIPIENTS,
    costCents: Math.round(seen.size * SMS_PRICE_CENTS),
  }
}

export function recipientSummary(recipients: Recipients, cost: SmsCost): string {
  const texts = recipients.people * Math.max(1, cost.segments)
  return `${recipients.people} ${tt('príjemcov')} × ${Math.max(1, cost.segments)} SMS = ${texts} ${tt('SMS')} · ${formatCents(Math.round(texts * SMS_PRICE_CENTS))}`
}

export type SendBlock = 'no-recipients' | 'no-message' | 'over-cap' | 'too-long' | 'no-credentials' | null

export function sendBlock(
  recipients: Recipients,
  message: string,
  hasCredentials: boolean,
): SendBlock {
  if (!hasCredentials) return 'no-credentials'
  if (recipients.people === 0) return 'no-recipients'
  if (message.trim() === '') return 'no-message'
  if (recipients.overCap) return 'over-cap'
  if (message.length > MAX_MESSAGE_CHARS) return 'too-long'
  return null
}

export function sendBlockMessage(block: SendBlock, recipients: Recipients): string {
  if (block === 'no-credentials') {
    return tt('Bez Application ID a tokenu BulkGate sa neodošle nič.')
  }
  if (block === 'no-recipients') return tt('Pridajte aspoň jedno telefónne číslo.')
  if (block === 'no-message') return tt('Napíšte text správy.')
  if (block === 'over-cap') {
    // Legacy sends the request anyway and shows *Chyba odosielania*, with nothing sent and no reason.
    return `${tt('Naraz sa dá odoslať najviac')} ${MAX_RECIPIENTS} ${tt('čísel; v zozname ich je')} ${recipients.entries.length}. ${tt('Server by celé odoslanie odmietol a neodoslal by nič.')}`
  }
  if (block === 'too-long') {
    return `${tt('Správa je dlhšia ako')} ${MAX_MESSAGE_CHARS} ${tt('znakov, server ju odmietne.')}`
  }
  return ''
}

/** The threshold is minutes and the job reads it every minute; a zero would chase every new order. */
export function clampThreshold(value: number): number {
  if (!Number.isFinite(value)) return 50
  return Math.min(480, Math.max(1, Math.round(value)))
}

// ─── The saves ────────────────────────────────────────────────────────────────

/**
 * What the settings save sends.
 *
 * The token is included **only** when somebody typed a new one. `update` writes just the keys the body
 * names, so omitting it leaves the stored credential alone — and it means the 50-character token does not
 * ride back and forth through the browser on every save.
 */
export function settingsPayload(form: SmsForm, newToken: string | null): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    deliveryEnabled: form.deliveryEnabled,
    deliverySenderName: form.deliverySenderName.trim(),
    deliveryTemplate: form.deliveryTemplate,
    // §9.322 - added with the fields, not after them. A textarea bound to state this omits looks
    // exactly like a working editor and changes nothing, which is the half `sms-settings.test.ts`
    // caught before the screen shipped.
    atAddressEnabled: form.atAddressEnabled,
    atAddressTemplate: form.atAddressTemplate,
    delayEnabled: form.delayEnabled,
    delayThresholdMinutes: clampThreshold(form.delayThresholdMinutes),
    delayTemplate: form.delayTemplate,
    delayMode: form.delayMode,
    bulkgateAppId: form.bulkgateAppId.trim(),
  }
  if (newToken !== null && newToken.trim() !== '') {
    payload[TOKEN_FIELD] = newToken.trim()
  }
  return payload
}

export function pointsSmsPayload(enabled: boolean, template: string): Record<string, unknown> {
  return { pointsSmsEnabled: enabled, pointsSmsTemplate: template }
}
